/**
 * The Android notification channel this app delivers push through.
 *
 * ── WHY A SHARED CONSTANT AND NOT A LITERAL ON EACH SIDE ────────────────────
 * Two unrelated places write this string: the app, which creates the channel
 * with `setNotificationChannelAsync` before it asks for a token, and the
 * Worker, which puts it in `message.android.notification.channelId` on every
 * FCM send. There is no compile-time link between those two strings — it is a
 * plain `string` field on the wire in both directions.
 *
 * And a MISMATCH DOES NOT FAIL. From FCM's own discovery document: *"if the
 * channel ID provided has not yet been created by the app, FCM uses the channel
 * ID specified in the app manifest"*. The notification still appears. It
 * appears through the manifest's default channel with whatever importance that
 * channel has — typically `DEFAULT` instead of `HIGH`: silent instead of
 * heads-up, no sound, low in the tray. FCM answers 200. Nothing in this
 * repository — not the type checker, not the suite, not the audit — can see it.
 * It reads as a subtle UX regression rather than as a bug.
 *
 * So: ONE constant, imported by `apps/mobile` and by `apps/api`. That is the
 * entire reason this lives in a shared package instead of beside either caller.
 *
 * ── AND THE VALUE IS EFFECTIVELY PERMANENT ──────────────────────────────────
 * Android freezes a channel's importance and its sound at CREATION. Calling
 * `setNotificationChannelAsync` again with different options changes nothing on
 * any device that already installed the app. A channel shipped with the wrong
 * importance can only be replaced by a NEW id, which orphans the old one in the
 * user's notification settings screen where they can still see and toggle it.
 *
 * Which is why the channel is created with `AndroidImportance.HIGH`. That
 * choice is permanent too, and `HIGH` is the one that leaves room: a user can
 * turn a channel DOWN in settings and can never turn one UP.
 */
export const NOTIFICATION_CHANNEL_ID = "mypocketgym-default";
/**
 * The three Android channels this app delivers through, one per KIND of
 * message (`D117`), all created with `AndroidImportance.HIGH`.
 *
 * ── WHY THREE AND NOT ONE ───────────────────────────────────────────────────
 * Android's own per-channel controls are the only place a person can turn one
 * kind of notification down without turning the rest off. With a single
 * channel, someone who silences the daily reminder also silences the
 * end-of-session summary and the morning check-in — and there is no signal
 * anywhere that they did. Three channels make the platform's settings screen
 * mirror the app's own three toggles (`notifications/preferences.ts`), which
 * is the only way those two can agree.
 *
 * ── EVERY WARNING ON {@link NOTIFICATION_CHANNEL_ID} APPLIES TO ALL THREE ───
 * A `channelId` FCM cannot match does not fail: the message is delivered
 * through the manifest's default channel, at that channel's importance,
 * and FCM answers 200. So these ids are written in exactly two places — the
 * app's `setNotificationChannelAsync` calls and the Worker's
 * `message.android.notification.channelId` — and both import this constant.
 *
 * And importance is frozen at CREATION on every device that has already
 * installed the app. `HIGH` is the choice that leaves room: a person can turn a
 * channel down and can never turn one up.
 *
 * ── THE IDS ARE PERMANENT ───────────────────────────────────────────────────
 * Changing one of these strings does not rename a channel. It creates a new
 * one, at whatever importance the new one is created with, and ORPHANS the old
 * one in the person's notification settings — visible, still toggleable, and
 * attached to nothing. Treat them as data, not as names.
 */
/**
 * The rest timer's alarm — a LOCAL notification, and the only one this app
 * schedules itself.
 *
 * ⚠️ IT IS NOT ONE OF THE THREE BELOW, AND THAT IS THE POINT. Those are
 * created by `ensureChannels` in `native/push.ts`, which runs only from
 * `enablePush` — the opt-in behind a button. **So on a phone where push was
 * never enabled, none of them exist.** A rest alarm pointed at one of them
 * would silently fall back to Capacitor's `default` channel for exactly the
 * people who never turned push on, which is worse than the bug it replaced.
 * This id is therefore created on the LOCAL path, beside the schedule that
 * uses it, and depends on nothing the push opt-in did.
 *
 * Measured on the owner's handset (T126, 2026-09-22) before this existed:
 * `LocalNotifications.schedule()` with no `channelId` landed on
 * `channel=default, importance=3`. Importance 3 does not peek — so the app's
 * most time-critical notification was the only one that never appeared over
 * the screen, and it showed up in system settings under a channel named
 * "Default" that nobody could recognise or tune. `schedule()` answers
 * `scheduled: true` either way, so nothing was red.
 *
 * Everything the block above says about permanence applies here too: the id
 * and the importance are frozen on every device that already created it.
 */
export const NOTIFICATION_CHANNEL_REST = "mypocketgym-rest";
export const NOTIFICATION_CHANNELS = {
    /** The daily "time to train" reminder, at the hour the person chose
     *  (`D100`). Collapse key `reminder`. */
    reminder: "mypocketgym-reminder",
    /** The morning-after soreness check-in (`D100`). Collapse key `session`. */
    checkin: "mypocketgym-checkin",
    /** The end-of-session summary, two minutes after the session ends
     *  (`D100`). Collapse key `session`. */
    session: "mypocketgym-session",
    /** The trainer answered while nobody was looking (`D192`). No collapse
     *  key: FCM keeps at most four per device and all four are taken
     *  (`PUSH_COLLAPSE_KEYS`), so each reply is its own message. */
    trainer: "mypocketgym-trainer",
};
//# sourceMappingURL=channel.js.map